import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusScreen from './StatusScreens';

const StatusContext = createContext(null);

const SLOW_MS = 8000;
let statusBus = null;

export function emitStatus(type, opts) {
  statusBus?.(type, opts);
}

export function StatusProvider({ children }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [pendingSlow, setPendingSlow] = useState(0);

  const show = useCallback((type, opts = {}) => {
    setStatus({ type, mode: opts.mode || (type === 'slow' ? 'toast' : 'full'), ...opts });
  }, []);

  const clear = useCallback(() => setStatus(null), []);

  useEffect(() => {
    statusBus = (type, opts) => show(type, opts);
    const onStatus = (e) => {
      const type = e.detail?.type;
      if (type) show(type);
    };
    window.addEventListener('nv-status', onStatus);
    return () => {
      statusBus = null;
      window.removeEventListener('nv-status', onStatus);
    };
  }, [show]);

  useEffect(() => {
    const goOffline = () => show('offline');
    const goOnline = () => {
      setStatus((s) => (s?.type === 'offline' ? null : s));
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    if (!navigator.onLine) goOffline();
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, [show]);

  useEffect(() => {
    const onSlowStart = () => setPendingSlow((n) => n + 1);
    const onSlowEnd = () => setPendingSlow((n) => Math.max(0, n - 1));
    window.addEventListener('nv-slow-start', onSlowStart);
    window.addEventListener('nv-slow-end', onSlowEnd);
    return () => {
      window.removeEventListener('nv-slow-start', onSlowStart);
      window.removeEventListener('nv-slow-end', onSlowEnd);
    };
  }, []);

  useEffect(() => {
    if (pendingSlow <= 0) {
      setStatus((s) => (s?.type === 'slow' ? null : s));
      return undefined;
    }
    const t = setTimeout(() => {
      if (!navigator.onLine) return;
      setStatus((s) => (s?.type === 'offline' || s?.type === 'timeout' || s?.type === 'denied' ? s : { type: 'slow', mode: 'toast' }));
    }, SLOW_MS);
    return () => clearTimeout(t);
  }, [pendingSlow]);

  const onPrimary = () => {
    if (!status) return;
    if (status.type === 'offline') {
      if (navigator.onLine) {
        clear();
        window.location.reload();
      }
      return;
    }
    if (status.type === 'slow') {
      clear();
      return;
    }
    if (status.type === 'denied') {
      clear();
      navigate('/');
      return;
    }
    if (status.type === 'timeout') {
      clear();
      navigate('/account');
    }
  };

  const value = useMemo(() => ({ show, clear }), [show, clear]);

  return (
    <StatusContext.Provider value={value}>
      {children}
      {status && (
        <StatusScreen
          type={status.type}
          mode={status.mode}
          onPrimary={onPrimary}
          onSecondary={status.mode === 'toast' || status.type === 'slow' ? clear : undefined}
          secondaryLabel={status.type === 'slow' ? 'Dismiss' : 'Close'}
        />
      )}
    </StatusContext.Provider>
  );
}

export function useStatus() {
  return useContext(StatusContext);
}
